import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { useRelativeDrag } from '../../lib/events-framework'
import { getProjectName } from '../../lib/get-project-name'
import { useElementHeight } from '../../lib/use-element-size'

export type Props = {
  maxHeight: string
  scroll?: number
  onScroll?: (position: number) => void
}

const snapInRange = (number: number, min: number, max: number) => Math.min(Math.max(number, min), max)

export const ScrollableContainer: FC<Props> = ({ children, scroll = 0, maxHeight, onScroll }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const visibleHeight = useElementHeight(containerRef)
  const contentHeight = useElementHeight(contentRef)

  const [scrollValue, setScrollValue] = useState(scroll)
  const [smooth, setSmooth] = useState(true)

  const visibleToContentRatio = visibleHeight !== null && contentHeight !== null ? visibleHeight / contentHeight : 1

  const maxScroll = visibleHeight !== null && contentHeight !== null ? contentHeight - visibleHeight : 0

  const scrollIsVisible = visibleHeight !== null && contentHeight !== null && contentHeight > visibleHeight

  const scrollBarPosition = scrollValue * visibleToContentRatio

  const scrollBarHeight = (visibleHeight || 0) * visibleToContentRatio

  const scrollTo = (value: number) => {
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = value
    })
  }

  useEffect(() => {
    scrollTo(scroll)
  }, [scroll])

  const { picked, pick } = useRelativeDrag({
    position: [0, scrollBarPosition],
    onPositionChange: ([_, scrollValue]) => scrollTo(snapInRange(scrollValue / visibleToContentRatio, 0, maxScroll)),
    onDrop: () => setSmooth(true)
  })

  useEffect(() => {
    onScroll && onScroll(scrollValue)
  }, [onScroll, scrollValue])

  return (
    <Wrapper onDragStart={(e) => e.preventDefault()}>
      <ScrollContainer
        smooth={smooth}
        isScrolling={picked}
        maxHeight={maxHeight}
        ref={containerRef}
        onScroll={(e) => {
          setScrollValue(e.currentTarget.scrollTop)
        }}
      >
        <div ref={contentRef}>{children}</div>
      </ScrollContainer>
      {scrollIsVisible && (
        <ScrollBar
          picked={picked}
          height={scrollBarHeight}
          style={{ transform: `translateY(${scrollValue * visibleToContentRatio}px)` }}
          onPointerDown={(e) => {
            setSmooth(false)
            pick(e)
          }}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
`

const ScrollContainer = styled.div<{ maxHeight: string; isScrolling: boolean; smooth: boolean }>`
  max-height: ${({ maxHeight }) => getProjectName() !== 'avito' && `${maxHeight}`};
  overflow-y: auto;
  user-select: ${({ isScrolling }) => (isScrolling ? 'none' : 'initial')};
  &::-webkit-scrollbar {
    display: none;
  }

  scroll-behavior: ${(props) => (props.smooth ? 'smooth' : 'initial')};

  & {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`

const ScrollBar = styled.div<{ height: number; picked: boolean }>`
  position: absolute;
  z-index: 100;
  top: 0;
  right: 0px;
  width: 10px;
  height: ${({ height }) => `${height}px`};
  padding: 2px;
  box-sizing: border-box;
  touch-action: none;
  user-select: none;

  &:before {
    content: '';
    display: block;
    height: 100%;
    background: ${({ theme, picked }) => (picked ? theme.palette.grey[500] : theme.palette.grey[300])};
    border-radius: 10px;
    transition: background-color 0.25s;
  }
`
